import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ChevronLeft, ChevronRight, CalendarDays, Sparkles, Tag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiFetch } from '@/lib/csrf'
import { nightsUntil } from '@/lib/calendar-utils'
import { nightsAway } from '@/lib/plain-language'
import DayDetailOverview from './DayDetailOverview'
import DayDetailInsights from './DayDetailInsights'
import DayDetailEditPrices from './DayDetailEditPrices'
import ManagedAtLeadCard from '@/components/groups/ManagedAtLeadCard'
import { isDeferredScope, leadLabel } from '@/lib/reporting-scope'
import type { ReportingScope } from '@/types/shared'
import type { PageIdentity } from '@/types/review'
import type { DayInfo, RoomType, RateData, OccupancyDay, AdrDay, RateHistoryEntry, BookingCurveData, RoomOccupancyEntry } from '@/types/calendar'

interface DayDetailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedDate: string
  selectedRoomTypeId?: number | null
  daysInMonth: DayInfo[]
  roomTypes: RoomType[]
  /** The rate map the CLICKING SURFACE rendered — pre-selected by the page so the
   *  modal shows byte-identical prices to the cell/hover that opened it. Prices
   *  merges the selected plan over the display plans for single-room views and
   *  passes the display map for multi-room views; Occupancy passes the display
   *  map its hover cards read. The modal never re-derives this. */
  rates: Record<string, RateData>
  occupancyData: OccupancyDay[]
  roomOccupancy?: Record<string, RoomOccupancyEntry>
  totalRooms: number
  currency: string
  /** The property's backend-served canonical path. */
  propertyBasePath: string
  /** Today AT THE PROPERTY (YYYY-MM-DD), resolved once by the page from the
   *  property's timezone. Every date judgement in here — the edit gate, the
   *  nights-away label, the range minimums — reads this one value, so the modal
   *  cannot classify a night as past while the hotel is still selling it. */
  today: string
  adrData?: Record<string, AdrDay>
  rateHistory?: Record<string, RateHistoryEntry[]>
  /**
   * Groups. A MEMBER'S calendar is a legitimate read surface, but it has no pricing controls: the
   * override endpoints this tab writes to answer a member with a structured 409 naming the lead
   * (PricingManagedAtLead::JSON_ACTIONS), because following a redirect would answer a question asked
   * about a member with the LEAD's numbers. So the Edit Prices tab is replaced by a pointer rather
   * than left to fail on save. Null/absent ⇒ ungrouped or the lead itself: the tab behaves exactly
   * as it always has.
   */
  reportingScope?: ReportingScope | null
  /**
   * The page's `pageIdentity`, handed to Edit Prices so its review names the hotel and the business
   * (change safety §5.10.4). Required, though it may be null, so neither calendar can forget it.
   */
  pageIdentity: PageIdentity | null | undefined
}

function formatDateLong(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
}

export default function DayDetailModal({
  open,
  onOpenChange,
  selectedDate: initialDate,
  selectedRoomTypeId,
  daysInMonth,
  roomTypes,
  rates,
  occupancyData,
  roomOccupancy,
  totalRooms,
  currency,
  propertyBasePath,
  today,
  adrData,
  rateHistory,
  reportingScope,
  pageIdentity,
}: DayDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'insights' | 'edit'>('overview')
  // A member has no pricing controls; the tab stays reachable so the answer is on screen where the
  // question was asked, but it holds a pointer to the lead instead of a form that 409s on save. Only a
  // member's Occupancy calendar reaches it: its Prices calendar is the lead's (server 302).
  const pricingDeferred = isDeferredScope(reportingScope)
  // COMBINED INVENTORY. Where the price form is offered (the lead, an ungrouped hotel) the rows are the
  // building's rooms, so no Same room is among them: a Same room takes no manual price of its own, and
  // the server's preview and review already list the Same rooms that move with a room.
  const [currentDate, setCurrentDate] = useState(initialDate)
  const hasDirtyEditsRef = useRef(false)
  const [curveData, setCurveData] = useState<BookingCurveData | null>(null)
  const [curveLoading, setCurveLoading] = useState(false)

  // Consistency invariant: the modal renders exactly the map the clicking surface
  // rendered. That map-selection now lives in the PAGE (Prices merges the selected
  // plan over display plans for single-room views and passes display plans for
  // multi-room views; Occupancy passes the display map its hover reads), so a
  // clicked cell and this modal can never diverge. Each entry carries plan
  // identity (serialize_rate stamps ratePlanId/ratePlanName), so the header chip
  // and per-row labels reflect that same map. The modal never merges or re-derives.

  // Reset state when modal opens with a new date
  useEffect(() => {
    if (open) {
      setCurrentDate(initialDate)
      setActiveTab('overview')
      hasDirtyEditsRef.current = false
    }
  }, [open, initialDate])

  const dateIndex = useMemo(
    () => daysInMonth.findIndex((d) => d.date === currentDate),
    [daysInMonth, currentDate]
  )

  const canGoPrev = dateIndex > 0
  const canGoNext = dateIndex < daysInMonth.length - 1

  // Gate day navigation behind unsaved-changes check
  const confirmIfDirty = useCallback((): boolean => {
    if (!hasDirtyEditsRef.current) return true
    return window.confirm('You have unsaved changes. Discard?')
  }, [])

  const goToPrev = useCallback(() => {
    if (canGoPrev && confirmIfDirty()) setCurrentDate(daysInMonth[dateIndex - 1].date)
  }, [canGoPrev, daysInMonth, dateIndex, confirmIfDirty])

  const goToNext = useCallback(() => {
    if (canGoNext && confirmIfDirty()) setCurrentDate(daysInMonth[dateIndex + 1].date)
  }, [canGoNext, daysInMonth, dateIndex, confirmIfDirty])

  // Keyboard nav — skip when focus is on an input/textarea
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToPrev() }
      if (e.key === 'ArrowRight') { e.preventDefault(); goToNext() }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, goToPrev, goToNext])

  // Preload booking curve data when modal opens or date changes. The effect
  // refires per currentDate, so each run owns an AbortController that cleanup
  // aborts — without it, an out-of-order response could paint a previous day's
  // curve/pace/RevPAR under the current day's header. On abort we do nothing;
  // on any other failure we fall back to the null empty-state (the Insights tab
  // has no dedicated error surface).
  useEffect(() => {
    if (!open) return
    const controller = new AbortController()
    setCurveLoading(true)
    ;(async () => {
      try {
        const response = await apiFetch(
          `${propertyBasePath}/calendar/booking_curve?date=${currentDate}`,
          { method: 'GET', signal: controller.signal }
        )
        if (controller.signal.aborted) return
        if (response.ok) {
          const data = (await response.json()) as BookingCurveData
          if (!controller.signal.aborted) setCurveData(data)
        } else {
          setCurveData(null)
        }
      } catch {
        if (controller.signal.aborted) return
        setCurveData(null)
      } finally {
        if (!controller.signal.aborted) setCurveLoading(false)
      }
    })()
    return () => controller.abort()
  }, [open, currentDate, propertyBasePath])

  // Extract season and event context from any rate's computation_details for this date
  const contextBadges = useMemo(() => {
    const badges: { label: string; variant: 'season' | 'event'; description: string; modifier: string }[] = []
    for (const rt of roomTypes) {
      const rate = rates[`${rt.id}:${currentDate}`]
      if (!rate?.computationDetails?.adjustments) continue
      for (const adj of rate.computationDetails.adjustments) {
        if (adj.stage === 'seasonal' && adj.type !== 'none') {
          const label = adj.description?.split(' ').slice(0, -1).join(' ') || 'Seasonal'
          if (!badges.some((b) => b.label === label)) {
            const mod = adj.modifier_pct ?? adj.value ?? 0
            badges.push({
              label,
              variant: 'season',
              description: 'Seasonal pricing adjustment',
              modifier: mod !== 0 ? `${mod >= 0 ? '+' : ''}${mod}% rate modifier` : 'No rate effect',
            })
          }
        }
        if (adj.stage === 'event' && adj.type !== 'none') {
          const label = adj.event_name || adj.description || 'Event'
          if (!badges.some((b) => b.label === label)) {
            const mod = adj.modifier_pct ?? adj.value ?? 0
            badges.push({
              label,
              variant: 'event',
              description: adj.description || 'Event pricing adjustment',
              modifier: mod !== 0 ? `${mod >= 0 ? '+' : ''}${mod}% demand boost` : 'No rate effect',
            })
          }
        }
      }
      if (badges.length > 0) break
    }
    return badges
  }, [roomTypes, rates, currentDate])

  const occupancyForDate = useMemo(
    () => occupancyData.find((d) => d.date === currentDate),
    [occupancyData, currentDate]
  )

  // Which rate plan(s) the displayed prices belong to — read straight from the
  // backend-stamped plan identity on each MERGED entry (display only, never
  // re-resolved here), so the chip reflects the post-merge reality (the
  // selected plan for its room, display plans for the rest). One shared name →
  // a single quiet header chip covers every tab; differing names → the chip
  // flags the mix and each room row labels its own plan.
  const planNames = useMemo(() => {
    const names: string[] = []
    for (const rt of roomTypes) {
      const name = rates[`${rt.id}:${currentDate}`]?.ratePlanName
      if (name && !names.includes(name)) names.push(name)
    }
    return names
  }, [roomTypes, rates, currentDate])
  const uniformPlanName = planNames.length === 1 ? planNames[0] : null
  const mixedPlans = planNames.length > 1

  // The PROPERTY's clock decides both, so the edit gate and the header's
  // leadNights label can never disagree — and neither can drift onto the
  // viewer's calendar. An operator two hours ahead of the hotel used to be told
  // tonight was already in the past.
  const isPastDate = currentDate < today

  const leadNights = useMemo(() => nightsUntil(currentDate, today), [currentDate, today])

  // Force off edit tab for past dates
  useEffect(() => {
    if (isPastDate && activeTab === 'edit') setActiveTab('overview')
  }, [isPastDate, activeTab])

  // Clear curve data when modal closes
  useEffect(() => {
    if (!open) setCurveData(null)
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The shell itself never scrolls — the header stays put across all three
          tabs, and each tab scrolls its own body so a tab with a footer (Edit
          Prices) can pin it. That is why this one dialog does not use
          DialogBody: it has THREE swappable bodies, and one of them (Edit
          Prices) owns its own scroll to pin an action bar. The wrapper below
          carries the body's ground colour so all three read the same. */}
      {/* No description: the title and the tabs say what this is. Saying so stops Radix warning. */}
      <DialogContent className="max-w-4xl min-h-[75vh] max-h-[85vh]" aria-describedby={undefined}>
        {/* Visually hidden title for accessibility */}
        <DialogTitle className="sr-only">
          Day detail for {formatDateLong(currentDate)}
        </DialogTitle>

        {/* Header — fixed; date nav, tabs and the plan chip stay visible while
            the tab body scrolls under them. */}
        <DialogHeader className="space-y-0 border-b border-border/40 pt-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={goToPrev}
                disabled={!canGoPrev}
                aria-label="Previous day"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-lg font-bold tracking-tight text-foreground">
                {formatDateLong(currentDate)}
                <span className="ml-2 text-sm font-medium text-muted-foreground">
                  {nightsAway(leadNights)}
                </span>
              </h2>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={goToNext}
                disabled={!canGoNext}
                aria-label="Next day"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <TooltipProvider delayDuration={150}>
            <div className="flex items-center gap-2">
              {contextBadges.map((b) => (
                <Tooltip key={b.label}>
                  <TooltipTrigger asChild>
                    <Badge
                      className={cn(
                        'text-[11px] font-medium cursor-default gap-1',
                        b.variant === 'season' && 'bg-blue-100 text-blue-700 hover:bg-blue-200/80',
                        b.variant === 'event' && 'bg-violet-100 text-violet-700 hover:bg-violet-200/80'
                      )}
                    >
                      {b.variant === 'season' ? (
                        <CalendarDays className="h-3 w-3" />
                      ) : (
                        <Sparkles className="h-3 w-3" />
                      )}
                      {b.label}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[220px]">
                    <p className="font-medium text-xs">{b.label}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{b.description}</p>
                    <p className={cn(
                      'text-[11px] font-medium mt-1',
                      b.modifier.startsWith('+') ? 'text-emerald-600' : b.modifier.startsWith('-') ? 'text-rose-600' : 'text-muted-foreground'
                    )}>
                      {b.modifier}
                    </p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
            </TooltipProvider>
          </div>

          {/* Tabs */}
          <div className="mt-3 flex items-center gap-1">
            <button
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                activeTab === 'overview'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                activeTab === 'insights'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
              onClick={() => setActiveTab('insights')}
            >
              Insights
            </button>
            <button
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                activeTab === 'edit'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                isPastDate && 'opacity-40 cursor-not-allowed'
              )}
              onClick={() => !isPastDate && setActiveTab('edit')}
              disabled={isPastDate}
            >
              {pricingDeferred ? 'Prices' : 'Edit Prices'}
            </button>

            {/* Rate-plan indicator — names the plan behind the displayed prices.
                Sits in the shared header so it covers all three tabs. */}
            {planNames.length > 0 && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-auto inline-flex max-w-[220px] cursor-default items-center gap-1.5 rounded-full border border-border/60 bg-muted/50 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                      <Tag className="h-3 w-3 shrink-0" />
                      <span className="truncate">{uniformPlanName ?? 'Mixed rate plans'}</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[240px]">
                    {uniformPlanName
                      ? `Prices shown are from the ${uniformPlanName} rate plan.`
                      : 'Rooms display prices from different rate plans — each price is labelled with its plan.'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </DialogHeader>

        {/* Tab content — hidden instead of unmounted to prevent flicker.
            Overview and Insights scroll as a whole; Edit Prices owns its own
            scroll so it can pin its action bar. */}
        <div className="flex min-h-0 flex-1 flex-col bg-muted/50">
          <div className={activeTab !== 'overview' ? 'hidden' : 'min-h-0 flex-1 overflow-y-auto scroll-py-3 py-5'}>
            <DayDetailOverview
              date={currentDate}
              roomTypes={roomTypes}
              rates={rates}
              occupancy={occupancyForDate}
              roomOccupancy={roomOccupancy}
              totalRooms={totalRooms}
              currency={currency}
              selectedRoomTypeId={selectedRoomTypeId ?? undefined}
              adrData={adrData}
              rateHistory={rateHistory}
              mixedPlans={mixedPlans}
              propertyBasePath={propertyBasePath}
            />
          </div>
          <div className={activeTab !== 'insights' ? 'hidden' : 'min-h-0 flex-1 overflow-y-auto scroll-py-3 py-5'}>
            <DayDetailInsights
              date={currentDate}
              curveData={curveData}
              curveLoading={curveLoading}
              occupancy={occupancyForDate}
              roomTypes={roomTypes}
              rates={rates}
              rateHistory={rateHistory}
              adrData={adrData}
              currency={currency}
              today={today}
              selectedRoomTypeId={selectedRoomTypeId}
              mixedPlans={mixedPlans}
            />
          </div>
          {activeTab === 'edit' && pricingDeferred && (
            <div className="min-h-0 flex-1 overflow-y-auto scroll-py-3 px-6 py-5">
              <ManagedAtLeadCard
                scope={reportingScope}
                what="Price edits"
                detail={`Prices for every room in this combined inventory are set at ${leadLabel(reportingScope)}, including manual prices on this listing's own rooms, which still publish under this listing.`}
                leadSuffix="/calendar/prices"
                ctaLabel={`Edit at ${leadLabel(reportingScope)}`}
              />
              <p className="mt-3 text-xs text-muted-foreground">
                Every price on this day belongs to the listing that owns the room and publishes under that
                listing. The Overview tab lists them room by room.
              </p>
            </div>
          )}
          {activeTab === 'edit' && !pricingDeferred && (
            // Fills the remaining height and manages its own scroll + pinned
            // action bar, so no wrapper here.
            <DayDetailEditPrices
              date={currentDate}
              roomTypes={roomTypes}
              rates={rates}
              currency={currency}
              propertyBasePath={propertyBasePath}
              isPastDate={isPastDate}
              today={today}
              mixedPlans={mixedPlans}
              pageIdentity={pageIdentity}
              onDirtyChange={(dirty: boolean) => { hasDirtyEditsRef.current = dirty }}
              onSaved={() => {
                // Stay open. The operator was shown "now → after" for every room and
                // should watch that number land: markRecomputeExpected plus the
                // rates_computed broadcast reload the page props, the form's awaiting
                // state clears on the new rates, and each saved row shows its landed
                // price. Closing here used to hide exactly the moment that proves the
                // form told the truth. The calendar behind repaints all the same.
                hasDirtyEditsRef.current = false
              }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
